import React, { useState } from 'react';
import { X, Mail, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Invoice } from '../../types';
import { invoicesApi } from '../../api/client';

// ─── Design tokens ────────────────────────────────────────────────────────────

const C = {
  bg: '#0a0a0f',
  card: '#12121a',
  border: '#1e1e2e',
  borderHover: '#2e2e42',
  textPrimary: '#e2e1f0',
  textSecondary: '#6b6880',
  textMuted: '#4a4858',
  orange: '#f97316',
  orangeHover: '#ea580c',
  success: '#22c55e',
  error: '#ef4444',
};

// ─── Types ────────────────────────────────────────────────────────────────────

type SendState = 'idle' | 'loading' | 'success' | 'error';

interface SendEmailModalProps {
  invoice: Invoice;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SendEmailModal({ invoice, onClose }: SendEmailModalProps) {
  const clientEmail = invoice.client?.email ?? '';
  const [email, setEmail] = useState(clientEmail);
  const [sendState, setSendState] = useState<SendState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const handleSend = async () => {
    if (!isValidEmail(email)) {
      setErrorMsg('Introduce un email válido');
      return;
    }
    setSendState('loading');
    setErrorMsg(null);
    try {
      await invoicesApi.sendEmail(invoice.id, email.trim());
      setSendState('success');
    } catch (err) {
      setSendState('error');
      if (err instanceof Error) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg('No se pudo enviar el email. Inténtalo de nuevo.');
      }
    }
  };

  const handleClose = () => {
    setSendState('idle');
    setErrorMsg(null);
    onClose();
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: C.bg,
    border: `1px solid ${errorMsg && sendState !== 'loading' ? 'rgba(239,68,68,0.5)' : C.border}`,
    borderRadius: 8,
    color: C.textPrimary,
    fontSize: 14,
    padding: '11px 14px 11px 42px',
    fontFamily: 'DM Sans, sans-serif',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s',
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.72)',
          zIndex: 300,
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 301,
        width: '90%', maxWidth: 460,
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        boxShadow: '0 24px 72px rgba(0,0,0,0.65)',
        overflow: 'hidden',
        animation: 'modalIn 0.2s cubic-bezier(0.16,1,0.3,1)',
      }}>

        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'rgba(249,115,22,0.12)',
            border: '1px solid rgba(249,115,22,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Mail size={18} color={C.orange} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{
              fontFamily: 'Syne, sans-serif', fontWeight: 800,
              fontSize: 17, color: '#fff', margin: 0, letterSpacing: '-0.02em',
            }}>
              Enviar Factura
            </h2>
            <p style={{ fontSize: 12, color: C.textSecondary, margin: 0 }}>
              {invoice.number}
              {invoice.client?.name ? ` · ${invoice.client.name}` : ''}
            </p>
          </div>
          <button
            onClick={handleClose}
            style={{
              width: 32, height: 32, borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.textSecondary,
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>

          {/* Success state */}
          {sendState === 'success' ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 14, padding: '12px 0 4px',
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'rgba(34,197,94,0.12)',
                border: '1px solid rgba(34,197,94,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CheckCircle2 size={26} color={C.success} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{
                  fontSize: 15, fontWeight: 700,
                  color: C.textPrimary, margin: '0 0 6px',
                  fontFamily: 'DM Sans, sans-serif',
                }}>
                  Email enviado correctamente
                </p>
                <p style={{ fontSize: 13, color: C.textSecondary, margin: 0 }}>
                  La factura fue enviada a <strong style={{ color: C.textPrimary }}>{email}</strong>
                </p>
              </div>
              <button
                onClick={handleClose}
                style={{
                  marginTop: 8,
                  padding: '9px 24px',
                  borderRadius: 9,
                  border: 'none',
                  background: `linear-gradient(135deg, ${C.orange}, ${C.orangeHover})`,
                  color: '#fff',
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(249,115,22,0.35)',
                }}
              >
                Cerrar
              </button>
            </div>
          ) : (
            <>
              <label style={{
                display: 'block',
                fontSize: 11, fontWeight: 600,
                color: C.textMuted, letterSpacing: '0.06em',
                fontFamily: 'JetBrains Mono, monospace',
                marginBottom: 8,
              }}>
                DIRECCIÓN DE EMAIL
              </label>

              {/* Email input with icon */}
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <Mail
                  size={14}
                  color={C.textMuted}
                  style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                />
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setErrorMsg(null); setSendState('idle'); }}
                  placeholder="cliente@empresa.es"
                  style={inputStyle}
                  onFocus={e => {
                    (e.target as HTMLInputElement).style.borderColor = 'rgba(249,115,22,0.5)';
                  }}
                  onBlur={e => {
                    (e.target as HTMLInputElement).style.borderColor =
                      errorMsg ? 'rgba(239,68,68,0.5)' : C.border;
                  }}
                  disabled={sendState === 'loading'}
                  autoFocus
                />
              </div>

              {/* Error message */}
              {(errorMsg || sendState === 'error') && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  marginBottom: 16, marginTop: 10,
                  padding: '9px 12px',
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: 8,
                }}>
                  <AlertCircle size={13} color={C.error} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: C.error }}>
                    {errorMsg || 'Error inesperado al enviar el email'}
                  </span>
                </div>
              )}

              {/* Info note */}
              {!errorMsg && sendState !== 'error' && (
                <p style={{ fontSize: 12, color: C.textMuted, margin: '8px 0 0', lineHeight: 1.5 }}>
                  Se adjuntará la factura {invoice.number} en formato PDF al email indicado.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer (only when not success) */}
        {sendState !== 'success' && (
          <div style={{
            padding: '16px 24px',
            borderTop: `1px solid ${C.border}`,
            display: 'flex', justifyContent: 'flex-end', gap: 10,
          }}>
            <button
              onClick={handleClose}
              style={{
                padding: '9px 18px', borderRadius: 9,
                border: `1px solid ${C.border}`,
                background: 'transparent',
                color: C.textSecondary,
                fontSize: 13, cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSend}
              disabled={sendState === 'loading' || !email.trim()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '9px 22px', borderRadius: 9,
                border: 'none',
                background: (sendState === 'loading' || !email.trim())
                  ? '#2a2a3a'
                  : `linear-gradient(135deg, ${C.orange}, ${C.orangeHover})`,
                color: (sendState === 'loading' || !email.trim()) ? C.textMuted : '#fff',
                fontSize: 13,
                cursor: (sendState === 'loading' || !email.trim()) ? 'not-allowed' : 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                fontWeight: 600,
                boxShadow: (sendState === 'loading' || !email.trim())
                  ? 'none'
                  : '0 4px 14px rgba(249,115,22,0.35)',
                transition: 'all 0.15s',
              }}
            >
              {sendState === 'loading' ? (
                <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} />
              ) : (
                <Send size={14} />
              )}
              {sendState === 'loading' ? 'Enviando...' : 'Enviar Factura'}
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes modalIn {
          from { opacity: 0; transform: translate(-50%, calc(-50% + 12px)); }
          to   { opacity: 1; transform: translate(-50%, -50%); }
        }
      `}</style>
    </>
  );
}
